<template>
    <div class="map-container" role="region" :aria-label="t('map_description')">
        <a href="#after-map" class="skip-link">{{ t('skip_map') }}</a>
        <div ref="mapContainer" class="map"></div>
        <button type="button" class="geo-button" :class="{ 'is-locating': isLocatingUser }" @click="locateUser">
            <span class="visuallyhidden">{{ t('user_position') }}</span>
            <IconGeolocation width="24" height="24" />
        </button>
        <button v-if="showRecenterButton" type="button" class="recenter-button button" @click="recenterMap('route')">
            {{ stop ? t('recenter_stop') : t('recenter') }}
        </button>
        <div id="after-map" tabindex="-1"></div>
    </div>
</template>

<script setup>
import 'maplibre-gl/dist/maplibre-gl.css';

import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { toGeoJSON } from 'geojson-polyline';
import maplibregl from 'maplibre-gl';
import GeoJSON from 'geojson';

import { useMap } from '@/composables/useMap';
import { useSchedulesStore } from '@@/js/stores/schedules';
import directionSlug from '@@/js/utils/directionSlug';
import slugify from '@@/js/utils/slugify';
import StopPopup from '@/schedules/components/StopPopup';

import IconGeolocation from '@@/icons/geolocation.svg';
import unservedStopUrl from '@@/img/unserved-stop.svg';
import stopMapPinUrl from '@@/img/stop-map-pin.svg';

const props = defineProps({
    selectedDate: {
        type: String,
        default: '',
    },
    route: {
        type: Object,
        default: null
    },
    direction: {
        type: Object,
        default: () => ({}),
    },
    stops: {
        type: Array,
        default: () => [],
    },
    stop: {
        type: Object,
        default: null,
    },
    defaultZoom: {
        type: Number,
        default: null,
    },
    hours: {
        type: Array,
        default: () => [],
    },
});

const { t } = useI18n({
    messages: {
        fr: {
            recenter: 'Voir tout le tracé',
            recenter_stop: `Voir l'arrêt`,
            user_position: 'Utiliser ma position',
            skip_map: 'Passer la carte',
            map_description: 'Carte interactive du parcours',
        },
        en: {
            recenter: 'See the whole path',
            recenter_stop: `View stop`,
            user_position: 'Use my position',
            skip_map: 'Skip map',
            map_description: 'Interactive route map',
        },
    },
});

const vRoute = useRoute();
const router = useRouter();
const schedulesStore = useSchedulesStore();

const routeLineSource = 'route-line';
const routeLineLayer = 'route-line-layer';
const stopsSource = 'stops';
const stopsLayer = 'stops-layer';
const unservedStopsLayer = 'unserved-stops-layer';
const unservedStopImage = 'unserved-stop';

const mapContainer = ref(null);
const { map, createMap, addMarker, setUserPosition, openPopup, onFeatureClick, loadImage, addCanvasImage, setGeoJSONSource } = useMap();
const userPosition = ref(null);
const isLocatingUser = ref(false);
const showRecenterButton = ref(false);
const firstMove = ref(true);
const selectedRoute = ref({});

const zoomLevel = computed(() => props.defaultZoom ?? (props.stop ? 16 : 11));

async function initializeMap() {
    createMap(mapContainer.value, {
        center: props.stop ? [props.stop.longitude, props.stop.latitude] : [-71.067, 46.81],
        zoom: zoomLevel.value,
    });

    onFeatureClick([stopsLayer, unservedStopsLayer], openStopPopup);

    map.value.on('moveend', () => {
        if (firstMove.value) {
            firstMove.value = false;
        } else {
            showRecenterButton.value = true;
        }
    });

    map.value.on('load', async () => {
        if (props.route) {
            await loadRouteAndStops();
        }

        if (props.stop) {
            const el = document.createElement('div');
            el.innerHTML = `<img src="${stopMapPinUrl}" class="current-stop" />`;
            addMarker(el, [props.stop.longitude, props.stop.latitude]);
            recenterMap('stop');
        } else {
            recenterMap('route');
        }
    });
}

async function loadRouteAndStops() {
    if (!props.route) return;

    const direction = props.direction?.code ?? '';
    const lines = await schedulesStore.fetchLine(props.route.noParcours, direction, 'all', props.selectedDate);

    selectedRoute.value.infos = props.route;
    selectedRoute.value.direction = direction;
    selectedRoute.value.line = lines[0];
    selectedRoute.value.geoJsonLine = toGeoJSON({
        type: 'MultiLineString',
        coordinates: lines.map((t) => t.polyligne),
    });
    selectedRoute.value.geoJsonStops = GeoJSON.parse(props.stops, { Point: ['latitude', 'longitude'] });

    if (props.hours) {
        selectedRoute.value.geoJsonStops.features.forEach((stopFeature) => {
            const stopData = props.hours.find((h) => h.arret.noArret === stopFeature.properties.noArret);
            stopFeature.properties.arretNonDesservi = stopData ? stopData.arretNonDesservi : false;
        });
    }

    addRouteLineLayer();
    await addStopsLayer();
}

function getRouteColor() {
    return schedulesStore.getServiceType(props.route.codeTypeService).color || '#003878';
}

function addRouteLineLayer() {
    setGeoJSONSource(routeLineSource, selectedRoute.value.geoJsonLine);

    if (!map.value.getLayer(routeLineLayer)) {
        map.value.addLayer({
            id: routeLineLayer,
            type: 'line',
            source: routeLineSource,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
                'line-color': getRouteColor(),
                'line-width': 5.5,
            },
        });
    }
}

async function addStopsLayer() {
    setGeoJSONSource(stopsSource, selectedRoute.value.geoJsonStops);

    if (map.value.getLayer(stopsLayer)) return;

    if (!map.value.hasImage(unservedStopImage)) {
        const img = await loadImage(unservedStopUrl);
        addCanvasImage(unservedStopImage, 24, 24, (context) => context.drawImage(img, 0, 0, 24, 24));
    }

    map.value.addLayer({
        id: stopsLayer,
        type: 'circle',
        source: stopsSource,
        filter: ['!=', ['get', 'arretNonDesservi'], true],
        paint: {
            'circle-color': getRouteColor(),
            'circle-radius': 5,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
        },
    });

    map.value.addLayer({
        id: unservedStopsLayer,
        type: 'symbol',
        source: stopsSource,
        filter: ['==', ['get', 'arretNonDesservi'], true],
        layout: {
            'icon-image': unservedStopImage,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
    });
}

function openStopPopup(feature) {
    const { noArret, nom } = feature.properties;
    const stopLink = {
        name: 'route-stop',
        params: {
            route: selectedRoute.value.infos.noParcours,
            direction: directionSlug(props.direction.code, props.direction.description),
            date: props.selectedDate,
            stop: `${noArret}-${slugify(nom)}`,
        },
    };

    openPopup(feature.geometry.coordinates, StopPopup, {
        noStop: noArret,
        name: nom,
        // No link to the stop page we are already on (the stop param is "<noArret>-<slug>").
        disabled: vRoute.name === 'route-stop' && vRoute.params.stop?.split('-')[0] === String(noArret),
        onClickAction() {
            router.push(stopLink).catch(() => {});
        },
    });
}

function updateUserLocationMarker() {
    if (!userPosition.value) return;

    setUserPosition([userPosition.value.coords.longitude, userPosition.value.coords.latitude]);
}

function recenterMap(position) {
    if (!map.value) return;

    showRecenterButton.value = false;
    firstMove.value = true;

    if (position === 'user' && userPosition.value) {
        showRecenterButton.value = true;
        firstMove.value = false;
        map.value.flyTo({
            center: [userPosition.value.coords.longitude, userPosition.value.coords.latitude],
            zoom: 13,
        });
    } else if (position === 'route') {
        if (props.stop) {
            map.value.flyTo({ center: [props.stop.longitude, props.stop.latitude], zoom: zoomLevel.value });
        } else if (map.value.getSource(routeLineSource)) {
            const bounds = new maplibregl.LngLatBounds();
            selectedRoute.value.geoJsonLine.coordinates.forEach((line) => {
                line.forEach((coord) => bounds.extend(coord));
            });
            map.value.fitBounds(bounds, { padding: 15 });
        }
    } else if (position === 'stop') {
        map.value.flyTo({ center: [props.stop.longitude, props.stop.latitude], zoom: zoomLevel.value });
    }
}

function locateUser() {
    isLocatingUser.value = true;
    navigator.geolocation.getCurrentPosition(
        (pos) => {
            userPosition.value = pos;
            isLocatingUser.value = false;
            updateUserLocationMarker();
            recenterMap('user');
        },
        () => {
            isLocatingUser.value = false;
        },
    );
}

onMounted(initializeMap);
</script>

<style lang="scss">
@import "vue/map";

.maplibregl-ctrl-attrib-inner {
    @include rem-typo(8px);
}
.maplibregl-popup-content {
    padding: 8px 32px 8px 16px;
}
.current-stop {
    display: block;
    width: 32px;
    height: 32px;
    max-width: none;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -100%);
}
</style>

<style lang="scss" scoped>
.map-container {
    position: relative;
    margin-top: 16px;
    border-radius: 15px;
    overflow: hidden;
    @include breakpoint(large) {
        border-radius: 0;
        margin: 0;
    }
}
.map {
    height: 100%;
}
.geo-button {
    position: absolute;
    right: 10px;
    bottom: 50px;
    width: 45px;
    height: 45px;
    border-radius: 50%;
    background-color: $color-white;
    box-shadow: $floating-shadow;
    transition: background-color $default-transition;
    border: 0;
    &:hover {
        background-color: $color-blue-60;
        svg {
            fill: $color-white;
        }
    }
    svg {
        fill: $color-blue-60;
        transition: fill $default-transition;
    }
    &.is-locating {
        animation-name: geoButtonPulsateBg;
        animation-duration: 2s;
        animation-iteration-count: infinite;
        svg {
            animation-name: geoButtonPulsateIcon;
            animation-duration: 2s;
            animation-iteration-count: infinite;
        }
    }
}
.recenter-button {
    position: absolute;
    right: 10px;
    bottom: 110px;
    box-shadow: $floating-shadow;
}
@keyframes geoButtonPulsateBg {
    from {
        background-color: $color-white;
    }
    50% {
        background-color: $color-blue-60;
    }
    to {
        background-color: $color-white;
    }
}
@keyframes geoButtonPulsateIcon {
    from {
        fill: $color-blue-60;
    }
    50% {
        fill: $color-white;
    }
    to {
        fill: $color-blue-60;
    }
}
</style>
